
import React from 'react';
import { 
  FaSearch,
  FaDownload,
  FaSync,
  FaPlus 
} from '../../../utils/iconUtils';
import { FaPrint } from 'react-icons/fa';

interface AttendanceFiltersProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onSearchSubmit: (e: React.FormEvent) => void;
  dateFilter: 'today' | 'week' | 'month' | 'custom' | 'all';
  onDateFilterChange: (value: 'today' | 'week' | 'month' | 'custom' | 'all') => void;
  startDate: string;
  onStartDateChange: (value: string) => void;
  endDate: string;
  onEndDateChange: (value: string) => void;
  onExport: () => void;
  onRefresh: () => void;
  onAddClick: () => void;
  onPrint: () => void;
  cacheStatus: string;
  canExport: boolean;
  canAdd: boolean;
  canPrint: boolean;
  hasRecords: boolean;
}

const AttendanceFilters: React.FC<AttendanceFiltersProps> = ({
  searchTerm,
  onSearchChange,
  onSearchSubmit,
  dateFilter,
  onDateFilterChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  onExport,
  onRefresh,
  onAddClick,
  onPrint,
  cacheStatus,
  canExport,
  canAdd,
  canPrint,
  hasRecords
}) => {
  return (
    <div className="flex flex-wrap gap-2 items-center">
      {/* Date Filter */}
      <div className="flex items-center gap-2">
        <select
          value={dateFilter}
          onChange={(e) => onDateFilterChange(e.target.value as any)}
          className="text-sm px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="today">Today</option>
          <option value="week">Last 7 Days</option>
          <option value="month">Last 30 Days</option>
          <option value="custom">Custom Range</option>
          <option value="all">All Time</option>
        </select>
        
        {dateFilter === 'custom' && (
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={startDate}
              onChange={(e) => onStartDateChange(e.target.value)}
              className="text-sm px-2 py-1 border border-gray-300 rounded-lg"
            />
            <span className="text-gray-400">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => onEndDateChange(e.target.value)}
              className="text-sm px-2 py-1 border border-gray-300 rounded-lg"
            />
          </div>
        )}
      </div>

      {/* Search */}
      <form onSubmit={onSearchSubmit} className="flex items-center">
        <div className="relative">
          <FaSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 h-3 w-3" />
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="text-sm pl-8 pr-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent w-40 lg:w-56"
          />
        </div>
        <button
          type="submit"
          className="ml-2 text-sm px-2 py-1 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
          title="Search"
        >
          <FaSearch className="h-3 w-3" />
        </button>
      </form>

      {/* Export */}
      {canExport && (
        <button
          onClick={onExport}
          className="flex items-center gap-2 text-sm px-2 py-1 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          title="Export CSV"
        >
          <FaDownload className="h-3 w-3 text-gray-600" />
          <span className="hidden lg:inline">Export</span>
        </button>
      )}

      {/* Refresh */}
      <button
        onClick={onRefresh}
        className="flex items-center gap-2 text-sm px-2 py-1 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
        title="Refresh from Firebase"
      >
        <FaSync className="h-3 w-3 text-gray-600" />
        <span className="hidden lg:inline">Refresh</span>
      </button>

      {/* Add New */}
      {canAdd && (
        <button
          onClick={onAddClick}
          className="flex items-center gap-2 text-sm px-3 py-1 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
          title="Add New Attendance"
        >
          <FaPlus className="h-3 w-3" />
          <span className="hidden lg:inline">Add</span>
        </button>
      )}

      {/* Print */}
      {canPrint && (
        <button
          onClick={onPrint}
          disabled={!hasRecords}
          className="flex items-center gap-2 text-sm px-2 py-1 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Print Report"
        >
          <FaPrint className="h-3 w-3 text-gray-600" />
          <span className="hidden lg:inline">Print</span>
        </button>
      )}
      
      <div className="text-xs text-gray-500 hidden lg:block">
        {cacheStatus}
      </div>
    </div>
  );
};

export default AttendanceFilters;
